import React, { useState, useEffect } from 'react'
import { Button, Container, Card, Row, Col, Form } from 'react-bootstrap'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import ApiEndpoints, {BASE_URL, BASE_URL_Upload} from './commonComponents/ApiEndpoints'
import axios from 'axios'
import Cookies from 'js-cookie'
import { 
  FaArrowLeft, 
  FaUser, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaVenusMars,
  FaGraduationCap
} from 'react-icons/fa'
import { 
  BsPersonBadge,
  BsCalendar3,
  BsFileEarmarkText,
  BsChevronDown,
  BsChevronUp,
  BsPencilFill
} from 'react-icons/bs'
import { useSidebar } from './SidebarContext'
import { useAuth } from './AuthContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { LuGraduationCap } from 'react-icons/lu'
import { useInstitute } from './commonComponents/InstituteContext'

const EditTeacherModal = ({ show, handleClose, teacher }) => {
  const { logout } = useAuth()
  const { logo } = useInstitute()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { teacher_id } = useParams()
  
  // State to store fetched teacher data
  const [fetchedTeacher, setFetchedTeacher] = useState(null)
  
  // Get teacher data from props, location state, or fetched teacher
  const teacherData = teacher || location.state?.teacher || fetchedTeacher

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const [updatedTeacher, setUpdatedTeacher] = useState({
    name: '',
    dob: '',
    qualification: '',
    phone: '',
    email: '',
    gender: '',
    address: '',
    password: '',
    joining_date: '',
  })
  const [teacherImage, setTeacherImage] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  
  // Section visibility states
  const [showPersonalInfo, setShowPersonalInfo] = useState(true)
  const [showProfessionalInfo, setShowProfessionalInfo] = useState(false)

  // Fetch teacher data if teacher_id is in URL but no teacher data available
  useEffect(() => {
    const institueId = Cookies.get('institute_id')
    
    if (!teacherData && teacher_id && institueId) {
      ApiEndpoints.getAllTeachers({ 'institute-Id': Number(institueId) })
        .then((res) => {
          if (Array.isArray(res.data.data)) {
            const matchedTeacher = res.data.data.find(
              (t) => String(t.id) === String(teacher_id),
            )
            if (matchedTeacher) {
              setFetchedTeacher(matchedTeacher)
            } else {
              console.error('No teacher found with ID:', teacher_id)
              toast.error('Teacher not found')
              navigate('/TeacherList')
            }
          }
        })
        .catch((err) => {
          console.error('Failed to fetch teachers:', err)
          toast.error('Failed to fetch teacher data')
          navigate('/TeacherList')
        })
    }
  }, [teacher_id, teacherData, navigate])

  useEffect(() => {
    if (teacherData) {
      setUpdatedTeacher({
        class_name: teacherData.class_name || '',
        name: teacherData.name || '',
        dob: teacherData.dob || '',
        qualification: teacherData.qualification || '',
        phone: teacherData.phone || '',
        email: teacherData.email || '',
        password: teacherData.password || '',
        gender: teacherData.gender || '',
        address: teacherData.address || '',
        joining_date: teacherData.joining_date || '',
      })
    }
  }, [teacherData])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setUpdatedTeacher((prev) => ({ ...prev, [name]: value }))
  }

  const handleBack = () => {
    if (handleClose) {
      handleClose()
    } else {
      navigate('/TeacherList')
    }
  }

  const handleSaveChanges = async () => {
    try {
      let imageUrl = null
      if (teacherImage) {
        if (teacherImage.size > 2 * 1024 * 1024) {
          toast.error('Image size should not exceed 2MB.')
          return
        }
        const formData = new FormData()
        formData.append('image', teacherImage)
        formData.append('folder', 'teacher')
        const imageResponse = await axios.post(
          BASE_URL+'/upload-image',
          formData,
        )
        imageUrl = BASE_URL_Upload + `/${imageResponse.data.path}`
      }
      const updatedData = {
        ...updatedTeacher,
        teacher_image: imageUrl,
      }

      const response = await ApiEndpoints.editTeacher(teacherData.id, updatedData)
      toast.success(response.data.message)
      if (handleClose) {
        handleClose()
      } else {
        navigate('/TeacherList')
      }
    } catch (error) {
      toast.error(error.response?.data?.message)
    }
  }

  const calculateAge = (dob) => {
    if (!dob) return ''
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--
    }
    return age
  }

  // If accessed via route but no teacher data, redirect back
  useEffect(() => {
    if (!teacherData && show !== true) {
      const timer = setTimeout(() => {
        navigate('/TeacherList')
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [teacherData, show, navigate])
  
  // Don't render if no teacher data and show prop is explicitly false
  if (show === false && !teacherData) {
    return null
  }

  return (
    <>
      <style>
        {`
          .info-label {
            font-size: 12px;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 6px;
            font-family: 'Inter', system-ui, sans-serif;
          }
          .info-value {
            font-size: 15px;
            font-weight: 500;
            color: #111827;
            font-family: 'Inter', system-ui, sans-serif;
          }
        `}
      </style>

      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {teacherData ? (
          <>
            {/* Header */}
            <div
              className="container-fluid p-0"
              style={{
                marginTop: window.innerWidth < 992 ? '55px' : '-12px',
                paddingLeft: '0px',
                paddingRight: '0px',
              }}
            >
              <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
                <div 
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    background: '#F3F4F6',
                    paddingLeft: '28px',
                    paddingRight: '28px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    overflow: 'visible',
                    borderBottom: '1px solid #D1D3D4',
                    width: '100%',
                  }}
                >
                  {/* Left: Title */}
                  <div className="d-flex align-items-center gap-3">
                    {/* School Logo */}
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      overflow: 'hidden', 
                      background: '#223843',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      flexShrink: 0,
                    }}>
                      {!logoError ? (
                        <img 
                          src={logo} 
                          alt="School Logo" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={() => setLogoError(true)}
                        />
                      ) : (
                        <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                      )}
                    </div>
                    
                    <h3 
                      className="m-0 fw-bold" 
                      style={{ 
                        letterSpacing: '-0.02em',
                        overflow: 'visible',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 'clamp(18px, 2.5vw, 22px)',
                        color: '#111827',
                        lineHeight: '1.2',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Update{' '}
                      {updatedTeacher.name
                        ? updatedTeacher.name.charAt(0).toUpperCase() +
                          updatedTeacher.name.slice(1).toLowerCase()
                        : 'Teacher'}'s Profile
                    </h3>
                  </div>
                  
                  {/* Right: Profile Icon with Dropdown */}
                  {!isMobile && (
                  <ProfileDropdown logout={logout} />
                  )}
                </div>
              </div>
            </div>

            <Container 
              className="mt-4"
              style={{
                paddingLeft: isCollapsed ? '32px' : '40px',
                paddingRight: '32px',
                paddingTop: '32px',
                paddingBottom: '32px',
              }}
            >
              {/* Profile Summary Section */}
              <Row className="mb-4">
                <Col xs={12} md={8}>
                  <Card 
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      height: '100%',
                    }}
                  >
                    <Card.Body style={{ padding: '40px', textAlign: 'center' }}>
                      {/* Profile Photo */}
                      <div className="mb-4" style={{ position: 'relative', display: 'inline-block' }}>
                        <label
                          htmlFor="teacherImageInput"
                          style={{ cursor: 'pointer' }}
                        >
                          {teacherData.teacher_image || teacherImage ? (
                            <div
                              style={{
                                width: '140px',
                                height: '140px',
                                borderRadius: '50%',
                                border: '4px solid #223843',
                                overflow: 'visible',
                                background: '#ffffff',
                                display: 'inline-block',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                position: 'relative',
                              }}
                            >
                              <img
                                src={
                                  teacherImage
                                    ? URL.createObjectURL(teacherImage)
                                    : teacherData.teacher_image
                                }
                                alt="Profile"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '50%',
                                }}
                              />
                              {/* Back to List Button */}
                              <button
                                onClick={handleBack}
                                style={{
                                  position: 'absolute',
                                  bottom: '-8px',
                                  right: '-8px',
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: '#223843',
                                  border: '3px solid #ffffff',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                  zIndex: 10,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.transform = 'scale(1.1)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.transform = 'scale(1)'
                                }}
                                title="Back to Teacher list"
                              >
                                <FaArrowLeft size={16} />
                              </button>
                            </div>
                          ) : (
                            <div
                              style={{
                                width: '140px',
                                height: '140px',
                                borderRadius: '50%',
                                border: '4px solid #223843',
                                background: '#F3F4F6',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                position: 'relative',
                              }}
                            >
                              <FaUser size={64} style={{ color: '#9ca3af' }} />
                              {/* Back to List Button */}
                              <button
                                onClick={handleBack}
                                style={{
                                  position: 'absolute',
                                  bottom: '-8px',
                                  right: '-8px',
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: '#223843',
                                  border: '3px solid #ffffff',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                  zIndex: 10,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.transform = 'scale(1.1)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.transform = 'scale(1)'
                                }}
                                title="Back to List"
                              >
                                <FaArrowLeft size={16} />
                              </button>
                            </div>
                          )}
                        </label>
                        <input
                          type="file"
                          id="teacherImageInput"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) {
                              if (file.size > 2 * 1024 * 1024) {
                                toast.error('Image size should not exceed 2MB.')
                                return
                              }
                              setTeacherImage(file)
                            }
                          }}
                        />
                      </div>

                      {/* Teacher Name */}
                      <h2 className='text-uppercase'
                        style={{
                          color: '#111827',
                          fontWeight: '700',
                          fontSize: 'clamp(24px, 3vw, 32px)',
                          marginBottom: '12px',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        {updatedTeacher?.name
                          ? updatedTeacher.name.charAt(0).toUpperCase() +
                            updatedTeacher.name.slice(1).toLowerCase()
                          : 'N/A'}
                      </h2>

                      {/* Email/Phone */}
                      <div className="mb-4">
                        <p 
                          style={{
                            color: '#6b7280',
                            fontSize: '16px',
                            marginBottom: '8px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <FaEnvelope size={16} />
                          {updatedTeacher?.email || updatedTeacher?.phone || 'N/A'}
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Save Button Card (Right) */}
                <Col xs={12} md={4}>
                  <Card 
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      background: 'linear-gradient(135deg, #F3F4F6 0%, #ffffff 100%)',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: isMobile ? '16px' : undefined,
                    }}
                  >
                    <Card.Body style={{ 
                      padding: '40px', 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}>
                      <div style={{ width: '100%' }}>
                        <div
                          style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#EFF6FF',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '20px',
                          }}
                        >
                          <BsPencilFill size={40} style={{ color: '#223843' }} />
                        </div>
                        <h5
                          style={{
                            color: '#111827',
                            fontWeight: '600',
                            fontSize: '18px',
                            marginBottom: '12px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Update Profile
                        </h5>
                        <p
                          style={{
                            color: '#6b7280',
                            fontSize: '14px',
                            marginBottom: '24px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Save changes to teacher information
                        </p>
                        <Button
                          onClick={handleSaveChanges}
                          className="w-100"
                          style={{
                            backgroundColor: '#223843',
                            border: 'none',
                            color: '#ffffff',
                            borderRadius: '12px',
                            padding: '14px 28px',
                            fontWeight: '600',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'translateY(-2px)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'translateY(0)'
                          }}
                        >
                          <BsPencilFill /> Save Changes
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Personal Information Section */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header with Icon */}
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                        padding: '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <FaUser size={18} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h5 className='text-start'
                              style={{
                                margin: 0,
                                fontWeight: '700',
                                color: '#ffffff',
                                fontSize: '18px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Personal Information
                            </h5>
                            <p
                              style={{
                                margin: '2px 0 0 0',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '12px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Update teacher details and contact information
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowPersonalInfo(!showPersonalInfo)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title={showPersonalInfo ? "Hide Section" : "Show Section"}
                        >
                          {showPersonalInfo ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                        </button>
                      </div>
                    </div>

                    {showPersonalInfo && (
                      <Card.Body style={{ padding: '20px' }}>
                        <Row className="g-3 text-start">
                          {/* Left Column */}
                          <Col xs={12} md={6}>
                            {/* Personal Details Card */}
                            <div
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: '12px',
                                padding: '14px',
                                border: '1px solid #E5E7EB',
                                marginBottom: '12px',
                              }}
                            >
                              <h6
                                style={{
                                  marginBottom: '10px',
                                  fontWeight: '600',
                                  color: '#10B981',
                                  fontSize: '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                              >
                                Personal Details
                              </h6>
                              <div className="d-flex flex-column gap-2">
                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <BsPersonBadge size={14} style={{ color: '#10B981' }} />
                                    <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Name</div>
                                  </div>
                                  <Form.Control
                                    type="text"
                                    name="name"
                                    value={updatedTeacher.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter Teacher's Name"
                                    className="border-0 p-0"
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#111827',
                                    }}
                                  />
                                </div>

                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <BsCalendar3 size={14} style={{ color: '#10B981' }} />
                                    <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Birth Date</div>
                                  </div>
                                  <Form.Control
                                    type="date"
                                    name="dob"
                                    value={updatedTeacher.dob}
                                    onChange={handleInputChange}
                                    className="border-0 p-0"
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#111827',
                                    }}
                                  />
                                </div>

                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <FaUser size={14} style={{ color: '#10B981' }} />
                                    <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Age</div>
                                  </div>
                                  <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                    {calculateAge(updatedTeacher?.dob) || 'N/A'} years
                                  </div>
                                </div>

                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <FaVenusMars size={14} style={{ color: '#10B981' }} />
                                    <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Gender</div>
                                  </div>
                                  <Form.Select
                                    name="gender"
                                    value={updatedTeacher.gender}
                                    onChange={handleInputChange}
                                    className="border-0 p-0"
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#111827',
                                      padding: '0',
                                    }}
                                  >
                                    <option value="">Select Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                  </Form.Select>
                                </div>
                              </div>
                            </div>
                          </Col>

                          {/* Right Column */}
                          <Col xs={12} md={6}>
                            {/* Contact Information Card */}
                            <div
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: '12px',
                                padding: '14px',
                                border: '1px solid #E5E7EB',
                                marginBottom: '12px',
                              }}
                            >
                              <h6
                                style={{
                                  marginBottom: '10px',
                                  fontWeight: '600',
                                  color: '#F59E0B',
                                  fontSize: '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                              >
                                Contact Information
                              </h6>
                              <div className="d-flex flex-column gap-2">
                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <FaEnvelope size={14} style={{ color: '#F59E0B' }} />
                                    <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Email Address</div>
                                  </div>
                                  <Form.Control
                                    type="email"
                                    name="email"
                                    value={updatedTeacher.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter Email ID"
                                    className="border-0 p-0"
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#111827',
                                    }}
                                  />
                                </div>

                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <FaPhone size={14} style={{ color: '#F59E0B' }} />
                                    <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Phone Number</div>
                                  </div>
                                  <Form.Control
                                    type="text"
                                    name="phone"
                                    value={updatedTeacher.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter Phone Number"
                                    className="border-0 p-0"
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#111827',
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Additional Information Card */}
                            <div
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: '12px',
                                padding: '14px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <h6
                                style={{
                                  marginBottom: '10px',
                                  fontWeight: '600',
                                  color: '#223843',
                                  fontSize: '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                              >
                                Additional Information
                              </h6>
                              <div className="d-flex flex-column gap-2">
                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <FaMapMarkerAlt size={14} style={{ color: '#10B981' }} />
                                    <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Address</div>
                                  </div>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="address"
                                    value={updatedTeacher.address}
                                    onChange={handleInputChange}
                                    placeholder="Enter Address"
                                    className="border-0 p-0"
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#111827',
                                    }}
                                  />
                                </div>

                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <BsFileEarmarkText size={14} style={{ color: '#223843' }} />
                                    <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Teacher Image</div>
                                  </div>
                                  <div className="d-flex align-items-center gap-3 mt-2">
                                    <Form.Control
                                      type="file"
                                      accept="image/png, image/jpeg, image/jpg"
                                      onChange={(e) => {
                                        const file = e.target.files[0]
                                        if (file) {
                                          if (file.size > 2 * 1024 * 1024) {
                                            toast.error('Image size should not exceed 2MB.')
                                            return
                                          }
                                          setTeacherImage(file)
                                        }
                                      }}
                                      style={{ fontSize: '13px' }}
                                    />
                                    {teacherImage && (
                                      <img
                                        src={URL.createObjectURL(teacherImage)}
                                        alt="Preview"
                                        style={{
                                          width: 60,
                                          height: 60,
                                          objectFit: 'cover',
                                          borderRadius: 8,
                                          border: '1px solid #E5E7EB',
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Professional Information Section */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header with Icon */}
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                        padding: '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <FaGraduationCap size={18} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h5
                              style={{
                                margin: 0,
                                fontWeight: '700',
                                color: '#ffffff',
                                fontSize: '18px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Professional Information
                            </h5>
                            <p className='text-start'
                              style={{
                                margin: '2px 0 0 0',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '12px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Update qualification and joining details
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowProfessionalInfo(!showProfessionalInfo)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title={showProfessionalInfo ? "Hide Section" : "Show Section"}
                        >
                          {showProfessionalInfo ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                        </button>
                      </div>
                    </div>

                    {showProfessionalInfo && (
                      <Card.Body style={{ padding: '20px' }}>
                        <Row className="g-3 text-start">
                          <Col xs={12} md={6}>
                            <div
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: '12px',
                                padding: '14px',
                                border: '1px solid #E5E7EB',
                                height: '100%',
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <div
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      borderRadius: '8px',
                                      backgroundColor: '#F3E8FF',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <FaGraduationCap size={16} style={{ color: '#9333EA' }} />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div className="info-label" style={{ marginBottom: '2px', fontSize: '11px' }}>Qualification</div>
                                    <Form.Control
                                      type="text"
                                      name="qualification"
                                      value={updatedTeacher.qualification}
                                      onChange={handleInputChange}
                                      placeholder="Enter Qualification"
                                      className="border-0 p-0"
                                      style={{
                                        fontSize: '15px',
                                        fontWeight: '700',
                                        color: '#111827',
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Col>
                          <Col xs={12} md={6}>
                            <div
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: '12px',
                                padding: '14px',
                                border: '1px solid #E5E7EB',
                                height: '100%',
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <div
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      borderRadius: '8px',
                                      backgroundColor: '#F3E8FF',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <BsCalendar3 size={16} style={{ color: '#9333EA' }} />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div className="info-label" style={{ marginBottom: '2px', fontSize: '11px' }}>Date of Join</div>
                                    <Form.Control
                                      type="date"
                                      name="joining_date"
                                      value={updatedTeacher.joining_date}
                                      onChange={handleInputChange}
                                      className="border-0 p-0"
                                      style={{
                                        fontSize: '15px',
                                        fontWeight: '700',
                                        color: '#111827',
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    )}
                  </Card>
                </Col>
              </Row>
            </Container>
          </>
        ) : (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <p style={{ color: '#6b7280', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Loading teacher data...
            </p>
          </div>
        )}
      </div>
    </>
  )
}

export default EditTeacherModal
